import axios from 'axios';
import config from '@/assets/config.json';
import store from '@/store';
import router from '@/router';

let refreshTokenPromise;

const apiClient = axios.create({
	baseURL: config.appApiUrl,
	withCredentials: false,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

const getRefreshToken = () => wrapper.post('v1/User/refresh', {
	token: store.getters['user/getToken'],
	refreshToken: store.getters['user/getRefreshToken']
}, {
	headers: {
		Authorization: `Bearer ${store.getters['user/getToken']}`
	}
}).then(data => {
	store.dispatch('user/setRefreshTokens', data, { root: true });

	return data;
}).catch(error => {
	console.log('login', error);

	router.push('/login');
});

apiClient.interceptors.response.use(
	res => {
		if ('x-pagination' in res.headers && Array.isArray(res.data.data)) {
			res.data.data._pagination = JSON.parse(res.headers['x-pagination']);
		}

		return res.data;
	},
	error => {
		if (error.config.url.includes('User/ResetPassword') || error.config.url.includes('User/ChangePassword')) {
			return Promise.reject(error);
		}

		if (error.config &&
			error.response &&
			error.response.status === 401 &&
			!error.config.url.includes('User/refresh') &&
			!error.config.url.includes('User/login')
		) {
			if (!refreshTokenPromise) {
				// check for an existing in-progress request
				// if nothing is in-progress, start a new refresh token request
				refreshTokenPromise = getRefreshToken().then(data => {
					refreshTokenPromise = null; // clear state

					return data; // resolve with the new token
				}).catch(error => {
					console.error(error);
				});
			}

			return refreshTokenPromise.then(data => {
				error.config.headers['Authorization'] = `Bearer ${data.token}`;

				return apiClient.request(error.config);
			});
		}

		return Promise.reject(error);
	}
);

apiClient.interceptors.request.use(
	config => {
		const token = store.getters['user/getToken'];

		if (token) {
			config.headers.Authorization = 'Bearer ' + token;
		}

		return config;
	},
	error => {
		Promise.reject(error);
	}
);

const callAsync = (promise, opts) => {
	const noop = () => {};
	const options = {
		throwException: typeof opts.throwException === 'boolean' ? opts.throwException : true,
		loading: typeof opts.loading === 'function' ? opts.loading : noop
	};

	options.loading(true);

	return new Promise((resolve, reject) => {
		promise
			.then(data => {
				const isResponseModel = typeof data === 'object' && 'success' in data;


				if (isResponseModel) {
					if (data.success) {
						return resolve(data.data);
					}

					const unsuccessfull = 'success' in data && !data.success;

					if (unsuccessfull && options.throwException) {
						throw new Error(data.errors && data.errors.length !== 0 ? data.errors[0] : 'Något gick fel');
					}
				}

				return resolve(data);
			})
			.catch(error => {
				if (options.throwException) {
					reject(error);
				}

				console.error(error.message);
			})
			.finally(() => {
				options.loading(false);
			});
	});
};

const filterParameters = params => {
	return Object.entries(typeof params === 'object' ? params : {}).reduce(
		(acc, [key, value]) => {
			return String(value).length !== 0 ? { [key]: value, ...acc } : acc;
		},
		{}
	);
};

const getOptions = (opts = {}) => {
	const options = typeof opts === 'object' ? opts : {};
	const { loading, throwException, ...axiosOpts } = options;
	const params = filterParameters(axiosOpts.params);

	return {
		axios: { ...axiosOpts, params },
		custom: { loading, throwException }
	};
};

const wrapper = {
	get: (url, opts) => {
		const options = getOptions(opts);

		return callAsync(apiClient.get(url, options.axios), options.custom);
	},

	post: (url, body, opts) => {
		const options = getOptions(opts);

		return callAsync(apiClient.post(url, body, options.axios), options.custom);
	},

	put: (url, body, opts) => {
		const options = getOptions(opts);

		return callAsync(apiClient.put(url, body, options.axios), options.custom);
	},

	delete: (url, opts) => {
		const options = getOptions(opts);

		return callAsync(apiClient.delete(url, options.axios), options.custom);
	}
};

export default wrapper;
